import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import {
  convertPercentageToMarks,
  deserify,
  getLocalStorage,
  IStyles,
  Loader,
  pxToRem,
  pxTovW,
  useCommonServiceClientContext,
} from '@geneo2-web/shared-ui';
import { CAOngoingHwLeftPanel } from './components/CAOngoingHwLeftPanel';
import { CAOngoingHwRightPanel } from './components/CAOngoingHwRightPanel';
import { useAppDispatch, useAppSelector } from '../../../reduxStore/reduxHooks';
import { setCurrentAssessmentClassStats } from '../reducer/manageHomework.slice';
import { AssessmentPageType } from '../ClickerAssessmentManage';
import { setToastInfo } from '../../Home/reducer/homeDashboard.slice';
import {
  IStatusObj,
  resetClickerAssessmentState,
  setClickerAssessmentInfo,
} from '../../Assessment/ClickerAssessment/reducer/clickerAssessment.slice';
import { Timestamp } from '@bufbuild/protobuf';
import { useGlobalContext } from '../../../app/Context/GlobalContextProvider';
import { useNavigate } from 'react-router-dom';
import {
  HOME,
  HOMEWORK_MANAGE,
} from '../../../routeHandling/RoutesNomenclature';
import {
  AttemptStats,
  AttemptTypeEnum,
} from '@protos/learning_management/lms.db_pb';

const styles: IStyles = {
  root: {
    width: '100vw',
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    justifyContent: { xs: 'unset', md: 'space-between' },
    gap: { xs: pxToRem(15), md: pxTovW(30), lg: pxTovW(30) },
    paddingY: { xs: pxToRem(20), md: pxTovW(15), lg: pxTovW(15) },
    paddingX: { xs: pxToRem(17), md: pxTovW(20), lg: pxTovW(240) },
    paddingTop: { xs: pxToRem(20), md: pxTovW(40) },
    boxSizing: 'border-box',
    overflowX: 'hidden',
  },

  leftPanel: {
    width: { xs: '100%', md: pxTovW(800), lg: pxTovW(602) },
    boxSizing: 'border-box',
  },

  rightPanel: {
    width: { xs: '100%', md: pxTovW(1000), lg: pxTovW(786) },
    boxSizing: 'border-box',
  },
};

interface IProps {
  pageType: AssessmentPageType;
  setIsAssessmentOngoing?: Dispatch<SetStateAction<boolean | null>>;
}
export const ClickerAssessmentOngoingHw = (props: IProps) => {
  const { pageType, setIsAssessmentOngoing } = props;
  const { setSelectedFunction, setSelectedLogoClickHandler } =
    useGlobalContext();
  const dispatch = useAppDispatch();
  const teacher_id = getLocalStorage('userId');
  const {
    LmsAssessmentTeacherAPIServiceV3WithStatusCodeHandler,
    LmsAssessmentTeacherAPIServiceV2WithStatusCodeHandler,
  } = useCommonServiceClientContext();
  const { selected_assessment_id, current_assessmemt_classStats } = deserify(
    useAppSelector((state) => state.manageHomework)
  );
  const { clicker_assessment_info } = deserify(
    useAppSelector((state) => state.clickerAssessment)
  );
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<'loading' | 'error' | 'success'>(
    'success'
  );

  // ----------------------------------------------------------------
  // const backButtonClick = (navigateType: 'back' | 'home') => {
  //   if (navigateType === 'back') {
  //     dispatch(resetClickerAssessmentState());
  //     navigate(HOMEWORK_MANAGE);
  //   } else if (navigateType === 'home') {
  //     dispatch(resetClickerAssessmentState());
  //     navigate(HOME);
  //   }
  // };
  // useEffect(() => {
  //   setSelectedFunction(() => () => backButtonClick('back'));
  //   setSelectedLogoClickHandler(() => () => backButtonClick('home'));

  //   return () => {
  //     setSelectedFunction(null);
  //     setSelectedLogoClickHandler(null);
  //   };
  // }, []);

  const getHomeworkDetailsOnEndTest = async () => {
    try {
      setIsLoading('loading');
      const response =
        await LmsAssessmentTeacherAPIServiceV3WithStatusCodeHandler.getAssessmentClassStats(
          {
            teacherId: BigInt(teacher_id),
            assessmentId: selected_assessment_id,
          }
        );
      if (response.data) {
        // console.log(response.data);
        const StatusObj: IStatusObj = {};
        response.data.studentPerformanceInfo.forEach((e) => {
          const id = e.studentId.toString();
          StatusObj[id] = {
            studentId: e.studentId,
            isSubmitted: e.isSubmitted || false,
            marks: convertPercentageToMarks(
              e.scorePercent,
              response.data?.assessment?.maxMarks
            ),
          };
        });
        const attempts: AttemptStats[] | undefined = response.data.attemptInfo;
        const latestAttempt =
          attempts?.find(
            (data) => data.attemptType === AttemptTypeEnum.ATTEMPT_TYPE_LATEST
          ) ?? attempts?.[attempts?.length - 1];
        dispatch(
          setClickerAssessmentInfo({
            ...clicker_assessment_info,
            statusInfo: StatusObj,
            selected_attempt: latestAttempt,
          })
        );
        dispatch(setCurrentAssessmentClassStats(response.data));
        setIsLoading('success');
      } else {
        dispatch(setClickerAssessmentInfo(undefined));
        dispatch(setCurrentAssessmentClassStats(undefined));
      }
    } catch (err) {
      setIsLoading('error');
      console.log(err);
    }
  };

  // ----------------------------------------------------------------

  const handleEndTest = async () => {
    setIsLoading('loading');

    const currentAssessment = current_assessmemt_classStats?.assessment;
    try {
      const response =
        await LmsAssessmentTeacherAPIServiceV2WithStatusCodeHandler.assessmentAssignmentSubmit(
          {
            assessmentId: selected_assessment_id,
            teacherId: currentAssessment?.teacherId,
            startTime: currentAssessment?.assessmentCommenceDate,
            endTime: Timestamp.fromDate(new Date()),
            assessmentInstructions: currentAssessment?.assessmentInstructions,
            sectionIds: currentAssessment?.sectionId
              ? [currentAssessment?.sectionId]
              : undefined,
            dailyReminderTime: currentAssessment?.dailyReminderTime,
            assignmentInfo: currentAssessment?.assignmentInfo,
            endAssessmentNow: true,
            assessmentName: currentAssessment?.assessmentTitle,
            modeOfConduction: currentAssessment?.modeOfConduction,
          }
        );
      if (response.status === 200 && response.data?.updatedAssessment) {
        getHomeworkDetailsOnEndTest(); // fetch updated current_assessment_classStats & clicker_assessment_info
        setIsAssessmentOngoing && setIsAssessmentOngoing(false);
        setIsLoading('success');
      } else {
        dispatch(
          setToastInfo({
            label: 'Something went wrong. Not able to end test.',
            variant: 'error',
            open: true,
          })
        );
        setIsLoading('error');
      }
    } catch (error) {
      dispatch(
        setToastInfo({
          label: 'Something went wrong. Not able to end test.',
          variant: 'error',
          open: true,
        })
      );
      setIsLoading('error');
      console.log(error);
    }
  };

  // ----------------------------------------------------------------

  if (isLoading === 'loading') return <Loader />;
  return (
    <Box sx={styles.root}>
      <Box sx={styles.leftPanel}>
        <CAOngoingHwLeftPanel
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          handleEndTest={handleEndTest}
          pageType={pageType}
          setIsAssessmentOngoing={setIsAssessmentOngoing}
        />
      </Box>

      <Box sx={styles.rightPanel}>
        <CAOngoingHwRightPanel pageType={pageType} />
      </Box>
    </Box>
  );
};
